/**
 * Waschleistung — im Leitstand-Stil (Phase B).
 *
 * Nutzt die bewährte Waschleistungs-API unverändert (inkl. Vergleich zum
 * Vorzeitraum) und ersetzt nur die Darstellung: Kennzahlen-Leiste und eine
 * kompakte Schichttabelle statt großer Diagramme. Die klassische Ansicht
 * bleibt unter /reports/wash-performance-klassisch erreichbar.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getTodayBerlin } from '@wash-ops/shared';
import { useAuth } from '../App';
import { LeitstandRahmen } from '../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kennzahl,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
} from '../components/leitstand';

interface ShiftDetail {
  date: string;
  shiftType: string;
  wsaKg: number;
  flKg: number;
  p25: number | null;
  p50: number | null;
  wlKg: number | null;
  hbwKg: number;
}

/** Ein Tag mit seinen Schichten — die Sicht, in der gemeldet wird. */
interface TagDetail {
  date: string;
  wsaKg: number;
  flKg: number;
  p25: number | null;
  p50: number | null;
  wlKg: number | null;
  hbwKg: number;
  schichten: ShiftDetail[];
}

interface Daten {
  label: string;
  totals: {
    wsaKg: number;
    flKg: number;
    p25Kg: number | null;
    p50Kg: number | null;
    wlKg: number | null;
    hbwKg: number;
    shiftsWithData: number;
  };
  shifts?: ShiftDetail[];
  tage?: TagDetail[];
  comparison?: { delta?: { wlPercent?: number | null; wsaPercent?: number | null } };
}

type Preset = 'today' | 'thisWeek' | 'thisMonth' | 'quartal' | 'frei';

const PRESETS: Array<{ schluessel: Preset; label: string }> = [
  { schluessel: 'today', label: 'Heute' },
  { schluessel: 'thisWeek', label: 'Diese Woche' },
  { schluessel: 'thisMonth', label: 'Dieser Monat' },
  { schluessel: 'quartal', label: '90 Tage' },
  { schluessel: 'frei', label: 'Zeitraum' },
];

function tagPlus(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function fmt(wert: number, nachkomma = 0): string {
  return wert.toLocaleString('de-DE', {
    minimumFractionDigits: nachkomma,
    maximumFractionDigits: nachkomma,
  });
}

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
function fmtTag(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WOCHENTAGE[d.getUTCDay()]} ${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
}

const SCHICHT_LABEL: Record<string, string> = {
  EARLY: 'FRÜH',
  LATE: 'SPÄT',
  SATURDAY: 'SAMSTAG',
};

export default function WashPerformanceLeitstand() {
  const { user } = useAuth();

  const [preset, setPreset] = useState<Preset>('thisWeek');
  const [freiVon, setFreiVon] = useState('');
  const [freiBis, setFreiBis] = useState('');
  const [daten, setDaten] = useState<Daten | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  // Die API zählt start UND end als Kalendertage MIT (inklusive) —
  // nachgemessen am 17.08.: [17..17] liefert den vollen Montag.
  const query = useMemo(() => {
    const heute = getTodayBerlin(new Date());
    if (preset === 'frei') {
      return freiVon && freiBis ? `range=custom&start=${freiVon}&end=${freiBis}` : null;
    }
    if (preset === 'today') return `range=custom&start=${heute}&end=${heute}`;
    if (preset === 'thisWeek') {
      const wochentag = new Date(`${heute}T00:00:00Z`).getUTCDay();
      return `range=custom&start=${tagPlus(heute, -((wochentag + 6) % 7))}&end=${heute}`;
    }
    if (preset === 'thisMonth') return `range=custom&start=${heute.slice(0, 8)}01&end=${heute}`;
    return `range=custom&start=${tagPlus(heute, -89)}&end=${heute}`;
  }, [preset, freiVon, freiBis]);

  const lade = useCallback(async () => {
    if (!query) return;
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch(`/api/reports/wash-performance?${query}&detail=shift&compare=1`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Die Auswertung konnte nicht geladen werden');
      }
      const body = await res.json();
      if (!body?.totals) throw new Error('Unerwartete Antwort vom Server');
      setDaten(body);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Auswertung konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [query]);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Gemeldet werden Tageswerte — die Schichten sind das Detail darunter.
  const tage = useMemo(() => daten?.tage ?? [], [daten]);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const klappe = (tag: string) =>
    setOffen((v) => {
      const neu = new Set(v);
      if (neu.has(tag)) neu.delete(tag);
      else neu.add(tag);
      return neu;
    });

  /** Summe über den angezeigten Zeitraum — die Zeile, die übertragen wird. */
  const summe = useMemo(() => {
    const oderNull = (f: (t: TagDetail) => number | null) =>
      tage.every((t) => f(t) === null) ? null : tage.reduce((a, t) => a + (f(t) ?? 0), 0);
    return {
      wsaKg: tage.reduce((a, t) => a + t.wsaKg, 0),
      p25: oderNull((t) => t.p25),
      p50: oderNull((t) => t.p50),
      hbwKg: tage.reduce((a, t) => a + t.hbwKg, 0),
      wlKg: oderNull((t) => t.wlKg),
      flKg: tage.reduce((a, t) => a + t.flKg, 0),
    };
  }, [tage]);

  return (
    <LeitstandRahmen
      titel="Waschleistung"
      benutzer={user?.displayName}
      werkzeuge={
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.schluessel}
                onClick={() => setPreset(p.schluessel)}
                aria-pressed={preset === p.schluessel}
                className={`rounded-[4px] border px-[11px] py-1.5 font-zahl text-xs transition-colors ${
                  preset === p.schluessel
                    ? 'border-akzent bg-akzent text-akzent-satz'
                    : 'border-linie-2 bg-flaeche text-tinte-2 hover:border-linie-stark'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'frei' && (
            <span className="flex items-center gap-1.5">
              <input
                type="date"
                value={freiVon}
                onChange={(e) => setFreiVon(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
              <span className="text-xs text-tinte-3">bis</span>
              <input
                type="date"
                value={freiBis}
                onChange={(e) => setFreiBis(e.target.value)}
                className="rounded-[4px] border border-linie-2 bg-flaeche px-2 py-1 font-zahl text-xs text-tinte"
              />
            </span>
          )}
          {/* Eine Datei mit genau den Zahlen der Tabelle — die, die gemeldet
              werden. Öffnet sich in Excel ohne Nachfrage. */}
          <a
            href={query ? `/api/reports/wash-performance/export?${query}` : undefined}
            aria-disabled={!query}
            className={`rounded-[4px] border border-linie-2 bg-flaeche px-[11px] py-1.5 font-zahl text-xs text-tinte-2 hover:border-akzent hover:text-akzent ${
              query ? '' : 'pointer-events-none opacity-50'
            }`}
          >
            Herunterladen
          </a>
        </div>
      }
    >
      {fehler && (
        <Notiz titel="Konnte nicht laden">
          {fehler}{' '}
          <button onClick={() => void lade()} className="underline">
            Erneut versuchen
          </button>
        </Notiz>
      )}

      {laden && !daten && (
        <p className="py-12 text-center text-sm text-tinte-3">Lade Auswertung …</p>
      )}

      {daten && (
        <div className={`flex flex-col gap-6 ${laden ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kennzahl
              marke="Waschleistung"
              wert={daten.totals.wlKg === null ? '—' : fmt(daten.totals.wlKg / 1000, 2)}
              einheit={daten.totals.wlKg === null ? undefined : 't'}
              neben={
                daten.comparison?.delta?.wlPercent == null
                  ? 'WSA + Taktwaschanlagen'
                  : `${daten.comparison.delta.wlPercent > 0 ? '+' : ''}${fmt(daten.comparison.delta.wlPercent, 1)} % zum Vorzeitraum`
              }
              tendenz={
                daten.comparison?.delta?.wlPercent == null
                  ? 'neutral'
                  : daten.comparison.delta.wlPercent >= 0
                    ? 'hoch'
                    : 'runter'
              }
            />
            <Kennzahl
              marke="Waschschleuderautomaten"
              wert={fmt(daten.totals.wsaKg / 1000, 2)}
              einheit="t"
              neben={`davon Bewohnerwäsche ${fmt(daten.totals.hbwKg / 1000, 2)} t`}
            />
            <Kennzahl
              marke="Taktwaschanlagen"
              wert={
                daten.totals.p25Kg === null && daten.totals.p50Kg === null
                  ? '—'
                  : fmt(((daten.totals.p25Kg ?? 0) + (daten.totals.p50Kg ?? 0)) / 1000, 2)
              }
              einheit="t"
              neben={
                daten.totals.p50Kg === null
                  ? undefined
                  : `P50 ${fmt((daten.totals.p50Kg ?? 0) / 1000, 2)} t · P25 ${fmt((daten.totals.p25Kg ?? 0) / 1000, 2)} t`
              }
            />
            <Kennzahl
              marke="Fertiggestellt"
              wert={fmt(daten.totals.flKg / 1000, 2)}
              einheit="t"
              neben={`${fmt(daten.totals.shiftsWithData)} Schichten mit Daten`}
            />
          </div>

          <Karte titel="Tage" marke={daten.label} tabelle>
            {tage.length === 0 ? (
              <p className="py-4 text-sm text-tinte-3">Keine Daten im Zeitraum.</p>
            ) : (
              <Tabelle>
                <thead>
                  <tr>
                    <Kopfzelle>Datum</Kopfzelle>
                    <Kopfzelle rechts>WSA</Kopfzelle>
                    <Kopfzelle rechts>P25</Kopfzelle>
                    <Kopfzelle rechts>P50</Kopfzelle>
                    <Kopfzelle rechts>HBW</Kopfzelle>
                    <Kopfzelle rechts>Waschleistung</Kopfzelle>
                    <Kopfzelle rechts>Fertigleistung</Kopfzelle>
                  </tr>
                </thead>
                <tbody>
                  {tage.map((t, i) => {
                    const letzte = i === tage.length - 1 && !offen.has(t.date);
                    const auf = offen.has(t.date);
                    return [
                      <tr key={t.date}>
                        <Zelle letzte={letzte}>
                          <button
                            onClick={() => klappe(t.date)}
                            aria-expanded={auf}
                            className="flex items-center gap-1.5 text-left hover:text-akzent"
                          >
                            <span
                              aria-hidden
                              className={`font-zahl text-[10px] transition-transform ${auf ? 'rotate-90' : ''}`}
                            >
                              ›
                            </span>
                            {fmtTag(t.date)}
                          </button>
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {fmt(t.wsaKg)} kg
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {t.p25 === null ? '—' : `${fmt(t.p25)} kg`}
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {t.p50 === null ? '—' : `${fmt(t.p50)} kg`}
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {fmt(t.hbwKg)} kg
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          <b className="text-akzent">
                            {t.wlKg === null ? '—' : `${fmt(t.wlKg)} kg`}
                          </b>
                        </Zelle>
                        <Zelle rechts zahl letzte={letzte}>
                          {fmt(t.flKg)} kg
                        </Zelle>
                      </tr>,
                      ...(auf
                        ? t.schichten.map((sch, j) => {
                            const letzteSch = i === tage.length - 1 && j === t.schichten.length - 1;
                            return (
                              <tr key={`${t.date}-${sch.shiftType}`} className="bg-flaeche-2">
                                <Zelle letzte={letzteSch}>
                                  <span className="pl-5">
                                    <Kennung>
                                      {SCHICHT_LABEL[sch.shiftType] ?? sch.shiftType}
                                    </Kennung>
                                  </span>
                                </Zelle>
                                <Zelle rechts zahl letzte={letzteSch}>
                                  {fmt(sch.wsaKg)} kg
                                </Zelle>
                                <Zelle rechts zahl letzte={letzteSch}>
                                  {sch.p25 === null ? '—' : `${fmt(sch.p25)} kg`}
                                </Zelle>
                                <Zelle rechts zahl letzte={letzteSch}>
                                  {sch.p50 === null ? '—' : `${fmt(sch.p50)} kg`}
                                </Zelle>
                                <Zelle rechts zahl letzte={letzteSch}>
                                  {fmt(sch.hbwKg)} kg
                                </Zelle>
                                <Zelle rechts zahl letzte={letzteSch}>
                                  {sch.wlKg === null ? '—' : `${fmt(sch.wlKg)} kg`}
                                </Zelle>
                                <Zelle rechts zahl letzte={letzteSch}>
                                  {fmt(sch.flKg)} kg
                                </Zelle>
                              </tr>
                            );
                          })
                        : []),
                    ];
                  })}
                  {/* Die Zeile, die übertragen wird. */}
                  <tr className="border-t-2 border-linie-2 font-medium">
                    <Zelle letzte>Summe</Zelle>
                    <Zelle rechts zahl letzte>
                      {fmt(summe.wsaKg)} kg
                    </Zelle>
                    <Zelle rechts zahl letzte>
                      {summe.p25 === null ? '—' : `${fmt(summe.p25)} kg`}
                    </Zelle>
                    <Zelle rechts zahl letzte>
                      {summe.p50 === null ? '—' : `${fmt(summe.p50)} kg`}
                    </Zelle>
                    <Zelle rechts zahl letzte>
                      {fmt(summe.hbwKg)} kg
                    </Zelle>
                    <Zelle rechts zahl letzte>
                      <b className="text-akzent">
                        {summe.wlKg === null ? '—' : `${fmt(summe.wlKg)} kg`}
                      </b>
                    </Zelle>
                    <Zelle rechts zahl letzte>
                      {fmt(summe.flKg)} kg
                    </Zelle>
                  </tr>
                </tbody>
              </Tabelle>
            )}
          </Karte>

          <p className="border-t border-linie pt-4 text-xs text-tinte-3">
            Waschleistung = Waschschleuderautomaten + Taktwaschanlagen.
          </p>
        </div>
      )}
    </LeitstandRahmen>
  );
}
